import {
  AssessmentType,
  MergedAssessmentData,
  mergeAssessmentData,
  getCacheKey,
  loadFromCache,
  saveToCache,
  clearCache,
  validateMergedData,
  ASSESSMENT_TYPE_CONFIG
} from './assessmentMerger';
import { API_CONFIG } from '../../config/api';

export interface FetchResult {
  data: Record<AssessmentType, MergedAssessmentData>;
  errors: Record<string, Error>;
}

/**
 * Fetches and merges assessment data for all assessment types
 * @param userId - User ID for cache key
 * @param skipCache - If true, clears cache before fetching to get fresh data
 */
export async function fetchAllAssessmentData(userId?: string, skipCache: boolean = false): Promise<FetchResult> {
  const cacheKey = getCacheKey(userId);
  const result: FetchResult = {
    data: {} as Record<AssessmentType, MergedAssessmentData>,
    errors: {}
  };

  // Clear cache if skipCache is true (for fresh data after assessment submission)
  if (skipCache) {

    clearCache(cacheKey);
  }

  // Load from cache first
  const cachedData = skipCache ? null : loadFromCache(cacheKey);

  // Fetch data for all assessment types concurrently
  const fetchPromises = Object.keys(ASSESSMENT_TYPE_CONFIG).map(async (type) => {
    const assessmentType = type as AssessmentType;

    try {
      const mergedData = await fetchAssessmentTypeData(assessmentType, cachedData?.[assessmentType]);
      result.data[assessmentType] = mergedData;
    } catch (error) {

      result.errors[assessmentType] = error as Error;

      // Use cached data as fallback if available
      if (cachedData?.[assessmentType]) {
        result.data[assessmentType] = cachedData[assessmentType];
      } else {
        // Provide empty fallback
        result.data[assessmentType] = {
          latest: null,
          history: [],
          isCompleted: false,
          lastUpdated: null
        };
      }
    }
  });

  await Promise.all(fetchPromises);

  // Save merged data to cache
  saveToCache(cacheKey, result.data);

  return result;
}

/**
 * Fetches data for a specific assessment type
 */
async function fetchAssessmentTypeData(
  assessmentType: AssessmentType,
  cachedData?: MergedAssessmentData
): Promise<MergedAssessmentData> {
  const config = ASSESSMENT_TYPE_CONFIG[assessmentType];

  // Fetch latest and history data concurrently
  const [latestResponse, historyResponse] = await Promise.allSettled([
    fetchLatestAssessment(assessmentType),
    fetchAssessmentHistory(assessmentType)
  ]);

  let latestData = null;
  let historyData: any[] = [];

  // Handle latest data response
  if (latestResponse.status === 'fulfilled') {
    latestData = latestResponse.value;
  } else {

  }

  // Handle history data response
  if (historyResponse.status === 'fulfilled') {
    historyData = historyResponse.value;
  } else {

  }

  // Merge the data
  const mergedData = mergeAssessmentData(assessmentType, latestData, historyData);

  // Validate merged data and clear cache if inconsistencies found
  const validation = validateMergedData(mergedData);
  if (!validation.valid) {

    // If we have cached data and current data has issues, prefer cache
    if (cachedData && (!latestData || historyData.length === 0)) {

      return cachedData;
    }
  }

  return mergedData;
}

/**
 * Fetches the latest assessment for a specific type
 */
async function fetchLatestAssessment(assessmentType: AssessmentType): Promise<any | null> {
  const config = ASSESSMENT_TYPE_CONFIG[assessmentType];
  const token = localStorage.getItem('token');

  const response = await fetch(config.latestEndpoint, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (response.ok) {
    const data = await response.json();
    if (data.success) {
      return data.data;
    }
  } else if (response.status === 404) {
    // 404 is expected when no assessment exists
    return null;
  } else {
    throw new Error(`Latest ${assessmentType} fetch failed: ${response.status}`);
  }

  return null;
}

/**
 * Fetches assessment history for a specific type
 */
async function fetchAssessmentHistory(assessmentType: AssessmentType): Promise<any[]> {
  const config = ASSESSMENT_TYPE_CONFIG[assessmentType];
  const token = localStorage.getItem('token');

  let url = config.historyEndpoint;
  let limit = '';

  // Add limits for performance
  if (assessmentType === 'mental-health') {
    limit = '?limit=50'; // Need more for category-based assessments
  } else {
    limit = '?limit=20';
  }

  const response = await fetch(url + limit, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (response.ok) {
    const data = await response.json();
    if (data.success && data.data) {
      // Handle different response structures
      if (assessmentType === 'legendarial' && data.data.assessments) {
        return data.data.assessments;
      }
      return Array.isArray(data.data) ? data.data : [data.data];
    }
  } else if (response.status === 404) {
    // 404 is expected when no history exists
    return [];
  } else {
    throw new Error(`History ${assessmentType} fetch failed: ${response.status}`);
  }

  return [];
}

/**
 * Refreshes data for a specific assessment type
 */
export async function refreshAssessmentType(
  assessmentType: AssessmentType,
  userId?: string
): Promise<MergedAssessmentData> {
  const cacheKey = getCacheKey(userId);

  try {
    const mergedData = await fetchAssessmentTypeData(assessmentType);

    // Update cache with new data for this type
    const existingCache = loadFromCache(cacheKey) || {} as any;
    existingCache[assessmentType] = mergedData;
    saveToCache(cacheKey, existingCache);

    return mergedData;
  } catch (error) {

    throw error;
  }
}

/**
 * Clears all cached assessment data
 */
export function clearAssessmentCache(userId?: string): void {
  const cacheKey = getCacheKey(userId);
  clearCache(cacheKey);
}



